import { createContext, useContext, useEffect, useState } from 'react'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/context/AuthContext'

export interface AppToast {
  id: string
  title: string
  message: string
  type: 'chat' | 'sms' | 'system'
  avatarUrl?: string
}

interface NotificationContextType {
  toasts: AppToast[]
  removeToast: (id: string) => void
  addToast: (toast: Omit<AppToast, 'id'>) => void
}

const NotificationContext = createContext<NotificationContextType | null>(null)

export function useNotifications() {
  const ctx = useContext(NotificationContext)
  if (!ctx) throw new Error('useNotifications must be used within NotificationProvider')
  return ctx
}

export function NotificationProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<AppToast[]>([])
  const { agent } = useAuth()

  const removeToast = (id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id))
  }

  const addToast = (t: Omit<AppToast, 'id'>) => {
    const id = Date.now().toString()
    setToasts(prev => [...prev, { ...t, id }])
    // Auto remove after 5 seconds
    setTimeout(() => {
      setToasts(prev => prev.filter(toast => toast.id !== id))
    }, 5000)
  }

  useEffect(() => {
    if (!agent) return

    const ch = supabase.channel('global_notifications')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'internal_team_messages' }, (payload) => {
        // Don't notify if the user sent it
        if (payload.new.sender_id === agent.id) return
        
        addToast({
          title: 'Team Vindication Chat',
          message: payload.new.content,
          type: 'chat'
        })
      })
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'sms_messages' }, (payload) => {
        // Only notify on inbound SMS
        if (payload.new.direction === 'inbound') {
          addToast({
            title: `SMS from ${payload.new.from_number}`,
            message: payload.new.body,
            type: 'sms'
          })
        }
      })
      .subscribe()

    return () => {
      supabase.removeChannel(ch)
    }
  }, [agent])

  return (
    <NotificationContext.Provider value={{ toasts, removeToast, addToast }}>
      {children}
    </NotificationContext.Provider>
  )
}
